import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Brain, Pencil, Plus, Search, Trash2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, apiError } from "@/lib/api";

type Mem = {
  id: string; type: string; subject: string | null; content: string;
  source: string; importance: number; is_active: boolean; created_at: string;
};

const TYPES = ["preference", "person", "company", "relationship", "project",
  "workflow", "fact", "instruction", "goal"];

export default function Memory() {
  const [items, setItems] = useState<Mem[]>([]);
  const [q, setQ] = useState("");
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ type: "preference", subject: "", content: "" });
  const [editId, setEditId] = useState<string | null>(null);
  const [editText, setEditText] = useState("");

  const load = useCallback(async (query = "") => {
    try {
      const url = query ? `/memory/search?q=${encodeURIComponent(query)}` : "/memory";
      setItems((await api.get(url)).data);
    } catch (e) { toast.error(apiError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.content.trim()) return;
    try {
      await api.post("/memory", { type: form.type, content: form.content, subject: form.subject || null });
      setForm({ type: "preference", subject: "", content: "" });
      setAdding(false);
      toast.success("Memory saved");
      load(q);
    } catch (e) { toast.error(apiError(e)); }
  };

  const saveEdit = async (id: string) => {
    try {
      await api.patch(`/memory/${id}`, { content: editText });
      setEditId(null);
      load(q);
    } catch (e) { toast.error(apiError(e)); }
  };

  const forget = async (id: string) => {
    try { await api.delete(`/memory/${id}`); toast.success("Forgotten"); load(q); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div data-testid="memory-page">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-semibold tracking-tight">Memory</h1>
          <p className="text-sm text-muted-foreground">What Remy remembers about you.</p>
        </div>
        <Button size="sm" className="rounded-full" data-testid="memory-add-toggle"
          onClick={() => setAdding((a) => !a)}>
          <Plus className="mr-1 h-4 w-4" /> Add
        </Button>
      </div>

      <div className="mb-4 flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} data-testid="memory-search"
            placeholder="Search memories…" className="rounded-full pl-9"
            onKeyDown={(e) => e.key === "Enter" && load(q)} />
        </div>
        <Button variant="secondary" className="rounded-full" data-testid="memory-search-btn"
          onClick={() => load(q)}>Search</Button>
      </div>

      {adding && (
        <div className="mb-4 space-y-3 rounded-2xl border border-border bg-card p-4" data-testid="memory-add-form">
          <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}
            data-testid="memory-type" className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm">
            {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })}
            placeholder="Subject (optional)" data-testid="memory-subject" />
          <Input value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })}
            placeholder="What should Remy remember?" data-testid="memory-content" />
          <Button className="w-full rounded-full" onClick={create} data-testid="memory-save">Save memory</Button>
        </div>
      )}

      <div className="space-y-2">
        {items.length === 0 && <p className="text-sm text-muted-foreground">No memories yet.</p>}
        {items.map((m) => (
          <div key={m.id} className="rounded-2xl border border-border bg-card p-4" data-testid={`memory-item-${m.id}`}>
            <div className="flex items-start justify-between gap-2">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-primary">
                <Brain className="h-3 w-3" /> {m.type}
              </span>
              <div className="flex gap-1">
                <button onClick={() => { setEditId(m.id); setEditText(m.content); }}
                  data-testid={`memory-edit-${m.id}`} className="text-muted-foreground hover:text-foreground">
                  <Pencil className="h-4 w-4" />
                </button>
                <button onClick={() => forget(m.id)} data-testid={`memory-forget-${m.id}`}
                  className="text-muted-foreground hover:text-destructive">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
            {m.subject && <p className="mt-2 text-xs font-medium">{m.subject}</p>}
            {editId === m.id ? (
              <div className="mt-2 flex items-center gap-2">
                <Input value={editText} onChange={(e) => setEditText(e.target.value)} data-testid={`memory-edit-input-${m.id}`} />
                <Button size="sm" className="rounded-full" onClick={() => saveEdit(m.id)} data-testid={`memory-edit-save-${m.id}`}>Save</Button>
                <button onClick={() => setEditId(null)}><X className="h-4 w-4 text-muted-foreground" /></button>
              </div>
            ) : (
              <p className="mt-1 text-sm">{m.content}</p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
